import type { LabConfig, TestResult } from '../types';
import { computeStats, now } from '../utils';

const GROUP = 'G_network';

async function measureFetchRtt(url: string, samples: number, timeoutMs: number): Promise<number[]> {
  const results: number[] = [];
  for (let i = 0; i < samples; i++) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    const t0 = now();
    try {
      await fetch(url, { method: 'HEAD', cache: 'no-store', signal: controller.signal });
    } catch {
      // Ignore errors/timeouts; we still want the timing attempt.
    } finally {
      window.clearTimeout(timeout);
    }
    const t1 = now();
    results.push(t1 - t0);
  }
  return results;
}

function collectIceCandidates(timeoutMs: number): Promise<{
  candidates: RTCIceCandidate[];
  localIps: string[];
  types: string[];
}> {
  return new Promise((resolve) => {
    const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
    const candidates: RTCIceCandidate[] = [];
    let done = false;

    function finish() {
      if (done) return;
      done = true;
      const localIps = candidates
        .map((c) => c.address)
        .filter((a): a is string => !!a);
      const types = candidates.map((c) => c.type).filter((t) => !!t) as string[];
      pc.close();
      resolve({ candidates, localIps: [...new Set(localIps)], types: [...new Set(types)] });
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) candidates.push(e.candidate);
    };

    pc.onicegatheringstatechange = () => {
      if (pc.iceGatheringState === 'complete') {
        finish();
      }
    };

    pc.createDataChannel('probe');
    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .catch(() => finish());

    setTimeout(finish, timeoutMs);
  });
}

export async function runNetworkTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];

  if (!config.enableNetwork) {
    tests.push({
      name: 'network_disabled',
      group: GROUP,
      success: false,
      durationMs: 0,
      error: 'Network tests disabled in config',
    });
    return tests;
  }

  try {
    const samples = await measureFetchRtt(config.endpoint, 3, 700);
    tests.push({
      name: 'fetch_rtt',
      group: GROUP,
      success: true,
      durationMs: samples.reduce((a, b) => a + b, 0),
      samples,
      stats: computeStats(samples),
      metadata: { samples: 3, timeoutMs: 700 },
    });
  } catch (err) {
    tests.push({
      name: 'fetch_rtt',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const iceT0 = now();
    const ice = await collectIceCandidates(1000);
    tests.push({
      name: 'webrtc_ice',
      group: GROUP,
      success: true,
      durationMs: now() - iceT0,
      metadata: {
        timeoutMs: 1000,
        candidateCount: ice.candidates.length,
        localIps: ice.localIps,
        candidateTypes: ice.types,
      },
    });
  } catch (err) {
    tests.push({
      name: 'webrtc_ice',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  return tests;
}
